import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, CircularProgress, Alert, IconButton, Breadcrumbs, Link } from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import {
  Policy, useGetEffectivePoliciesQuery, useDeletePolicyMutation, useGetReposQuery,
} from '../../store/api/githubApi';
import { PolicyList } from '../../components/policies/PolicyList';
import { PolicyFormDialog } from '../../components/policies/PolicyFormDialog';
import { useNotification } from '../../hooks/useNotification';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';

export const RepoPoliciesPage: React.FC = () => {
  const { repoId } = useParams<{ repoId: string }>();
  const repositoryId = Number(repoId);
  const navigate = useNavigate();

  const { data: repos } = useGetReposQuery();
  const repo = repos?.find((r) => r.id === repositoryId);

  const { data: effectivePolicies, isLoading, error } = useGetEffectivePoliciesQuery(repositoryId, {
    skip: !repositoryId,
  });
  const [deletePolicy] = useDeletePolicyMutation();
  const { notification, hideNotification, showSuccess, showError } = useNotification();

  const [formOpen, setFormOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);

  const handleNew = () => {
    setEditingPolicy(null);
    setFormOpen(true);
  };

  const handleEdit = (policy: Policy) => {
    setEditingPolicy(policy);
    setFormOpen(true);
  };

  const handleDelete = async (policy: Policy) => {
    if (!window.confirm('Delete this repo override? It will fall back to the account-wide or global policy.')) return;
    try {
      await deletePolicy(policy.id).unwrap();
      showSuccess('Policy override deleted.');
    } catch (err: any) {
      showError(err?.data?.detail ?? 'Failed to delete policy.');
    }
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => navigate('/repositories')}>
          Repositories
        </Link>
        <Typography color="text.primary">{repo?.full_name ?? 'Policies'}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate('/repositories')} sx={{ mt: 0.25 }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>Policies</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
              The effective PR gate rules for this repo — repo-specific overrides win over account-wide
              overrides, which win over the global default. Add an override here to change behavior for
              just this repo.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleNew} disabled={!repositoryId}>
          New override
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ px: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>Failed to load policies.</Alert>
        ) : (
          <PolicyList
            policies={effectivePolicies ?? []}
            editableTier="repo"
            onEdit={handleEdit}
            onDelete={handleDelete}
            emptyMessage="No policies in effect."
          />
        )}
      </Paper>

      <PolicyFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        repositoryId={repositoryId}
        editingPolicy={editingPolicy}
      />

      <NotificationSnackbar notification={notification} onClose={hideNotification} />
    </Box>
  );
};

export default RepoPoliciesPage;
